import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { Summary } from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';

import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';

const backEndToolsImg = require('../../assets/images/icon-illustration/back-end-tools.svg');
const consultingImg = require('../../assets/images/illustration/consulting.svg');
const customSoftwareImg = require('../../assets/images/icon-illustration/custom-software.svg');
const dataImg = require('../../assets/images/illustration/data.svg');
const frontEndToolsImg = require('../../assets/images/icon-illustration/front-end-tools.svg');
const genericImg = require('../../assets/images/illustration/generic.svg');
const researchImg = require('../../assets/images/icon-illustration/enterprise-ux-research.svg');
const toolsImg = require('../../assets/images/illustration/tools.svg');

const factory = create();

export const EnterpriseArchitecture = factory(function EnterpriseArchitecture() {
	return (
		<div>
			<Head
				title="Enterprise Architecture Consulting Services | SitePen"
				description="Enterprise architecture is vital to digital transformation. SitePen’s suite of services helps companies easily adapt to changing digital environments."
			/>

			<Hero background="level2RedOrange">
				{{
					xlTitle: (
						<div>
							Enterprise
							<br />
							Architecture
						</div>
					)
				}}
			</Hero>

			<Section>
				<ContentWrapper>
					<Summary reverse>
						{{
							title: 'The Benefits of Effective Enterprise Architecture',
							blurb: [
								<p>
									Maintaining a diverse suite of software and information assets
									across several business divisions presents a range of challenges
									that are difficult to address without some degree of
									coordination and standardization. Enterprise Architecture
									addresses this need across the technology capabilities and
									solutions within an organization, providing a framework that
									shapes and streamlines how such solutions are created and
									utilized to the best of their capabilities.
								</p>,
								<p>
									Enterprise architecture aims to align overall technology
									implementation and direction to best meet the business and
									executive strategies of the broader enterprise. Individual
									software solutions that factor in enterprise architecture
									requirements are best placed to address their own specific
									business needs in a way that efficiently balances other
									important enterprise constraints such as re-use, integrations,
									and cost - resulting in a greater return on investment.
								</p>
							],
							image: <Image src={consultingImg} size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Enterprise Architecture Services',
							blurb: (
								<virtual>
									SitePen offers a variety of consulting services that can help
									optimize and enhance your existing enterprise architecture and
									its implementation, both across multiple and within individual
									technology solutions. For newer organizations burgeoning to a
									point where managing multiple systems is proving challenging,
									SitePen is able to advise on and help implement a solid
									enterprise architecture practice to help streamline oversight
									and maintenance of current and future software solutions.
									SitePen’s expertise in end-to-end solution delivery can provide
									help at every stage of enterprise architecture design,
									maintenance, and implementation - working with your existing
									implementation teams, or operating as a complete standalone
									solution delivery function.
								</virtual>
							),
							items: [
								{
									title: 'Analysis and Strategy',
									blurb: [
										<p>
											SitePen can help with a range of business process
											analysis and enterprise architecture strategy
											formulation, whether the goal is how best to pursue and
											integrate new opportunities, or when trying to find ways
											of streamlining utilization and cost-effectiveness
											across an existing set of technology capabilities.
										</p>,
										<p>
											SitePen can work within and across organization
											divisions to help analyze business process needs, data
											flows, systems integrations, enumerate and assess
											architecture across existing technology capabilities, as
											well as identify any relevant organizational or
											technology constraints. This analysis is used to define
											an enterprise architecture strategy that identifies an
											optimal set of technology capabilities that can best
											meet all given constraints, and that aligns overall
											technology direction with the enterprise’s wider
											strategic aims.
										</p>
									],
									image: (
										<Image src={researchImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'Planning and Design',
									blurb:
										'SitePen can help in the planning of how best to implement an established enterprise architecture strategy, as well as any associated solution design needed to implement new technology systems, data repositories, and process flows within the strategic framework. Our planning and design expertise can also help find the best way of adapting existing solutions to more closely align with the wider enterprise architecture framework, helping to rationalize capabilities across multiple software assets with an aim of increasing asset utilization and return on investment.',
									image: (
										<Image
											src={frontEndToolsImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Implementation and Management',
									blurb:
										'Architecture is not effective if treated as an ivory tower concept - it requires close alignment with implementation teams to ensure the larger strategies can be translated to working software. Teams should understand how and why particular decisions have been made so that everyone is pulling in the same direction. It is important to embed ongoing architectural design and alignment phases within all software delivery lifecycles across the enterprise, to ensure that individual solutions remain viable within the constraints of the overall enterprise architecture strategy. SitePen can work with implementation teams to ensure effective translation of enterprise architecture to solution architecture and implementation, as well as operate as a complete solution delivery team factoring in all required architecture concerns, including ongoing maintenance.',
									image: (
										<Image
											src={customSoftwareImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Optimization and Support',
									blurb:
										'Enterprise architecture is most effective when it can evolve over time as part of an interactive solution implementation process. This allows for continuous refinement of both the enterprise architecture and individual solution implementations, to ensure all levels of technology strategy and implementation best address ever-changing business needs as well as advancements in the wider technology industry. Ongoing support and optimization is therefore a key part of SitePen’s enterprise architecture implementation capability.',
									image: (
										<Image
											src={backEndToolsImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper responsive>
					<SummaryList
						listGuttering="none"
						trailingBias="medium"
						itemPaddingBottom="md"
						style="alternating-reverse"
					>
						{{
							title: 'Enterprise Architecture Experience',
							items: [
								{
									title: 'Enterprise Data Architecture',
									blurb:
										'The management and flow of information is a key consideration within business processes as well as the software solutions that implement such processes. The majority of business value is derived from the data being processed rather than the processes themselves. Enterprise data architecture therefore forms a critical piece of SitePen’s enterprise architecture consulting capability. Our expertise covers a wide range of concerns around data modelling, database architecture, data flow design, integrations, and migrations. Information security is a critical consideration at every step.',
									image: <Image src={genericImg} lazy size="lg" />
								},
								{
									title: 'Enterprise Architecture Tools',
									blurb: [
										<p>
											SitePen’s approach to enterprise architecture tooling is
											pragmatic, preferring lightweight documentation
											(including a range of diagramming and modelling) that
											evolves over time as architecture is proven across
											real-world solutions. We typically work with commodity
											off-the-shelf documentation and information repository
											solutions, favoring ease of access to architectural
											documentation across the enterprise - as well as
											avoiding vendor-specific lock-in.
										</p>,
										<p>
											Where appropriate, lightweight tooling may be useful to
											help automate certain data schema modelling and systems
											scaffolding that can be derived directly from the
											architectural blueprints. This helps to create “living
											architecture” that is kept up-to-date with evolving
											requirements and implementation details, allowing it to
											better retain its value as an accurate design authority
											over time - as well as benefit from a degree of process
											automation.
										</p>
									],
									image: <Image src={toolsImg} lazy size="lg" />
								},
								{
									title: 'Enterprise Architecture Framework',
									blurb:
										'SitePen’s approach to an enterprise architecture framework can be considered “TOGAF-like” in covering all relevant architecture domains, without necessarily requiring full TOGAF certification. Part of our expertise centers around our agility, which translates into favoring “agile architecture” that proves its value through being both lightweight and comprehensive. Our approach can be tailored to fit business and enterprises of all scales, giving the benefits that robust enterprise architecture can provide in a way that is easy to understand, rationalize, implement, and maintain over time.',
									image: <Image src={dataImg} lazy size="lg" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Enterprise FAQs">
					{[
						{
							title: 'What is Enterprise Application Architecture?',
							content: (
								<p>
									Enterprise application architecture is the framework that guides
									and supports software and data solutions across an enterprise to
									better align with business process requirements, as well as
									broader strategic directives guiding business and technology
									direction. The framework encompasses documentation that lays out
									architecture designs and reasoning behind key decisions, as well
									as tooling to surface the documentation across solution teams.
									Processes and communication channels are also required to allow
									enterprise architecture to disseminate out to application design
									and implementation teams, as well as filter back in to allow
									bi-directional adjustments and refinements to keep enterprise
									and solution architecture in sync.
								</p>
							)
						},
						{
							title: 'What Is the Purpose of Enterprise Architecture?',
							content: (
								<p>
									The fundamental purpose of enterprise architecture is simple -
									to design and support optimal ways that software and information
									technology can best serve immediate and future business needs.
									It aims to answer the “what”, “how” and most importantly “why”
									around implementation of software technology solutions that
									minimize unnecessary waste while best supporting existing and
									anticipated business opportunities, within and across
									organizational boundaries. This purpose extends throughout an
									organization’s lifetime, meaning enterprise architecture is only
									valuable when it is able to adapt to ever-changing business
									needs, constraints, and broader industry developments.
								</p>
							)
						},
						{
							title: 'Why Is Enterprise Architecture Important?',
							content: [
								<p>
									Businesses, divisions, and solution teams tend to focus on their
									own immediate concerns, which can result in opportunities being
									overlooked where repurposing or adapting solutions that already
									exist elsewhere in the enterprise may address requirements in
									more cost- and time-effective ways. Opportunities can also be
									missed where broader integrations across divisions could help
									provide greater value than the sum of their isolated parts.
									Without the guidance that robust enterprise architecture
									provides, significant waste can be generated in these ways where
									short-term solutions are implemented to meet apparent needs, but
									are ultimately abandoned for a wide variety of reasons.
								</p>,
								<p>
									In the same way that executive strategy helps point businesses
									and divisions into synergistic rather than conflicting
									directions, enterprise architecture helps align technology and
									data solutions to sustainably meet business needs while
									minimizing waste and overlap. Effective enterprise architecture
									helps bridge the divide between addressing valuable immediate
									business opportunities, but doing so in a way that addresses a
									range of constraints and that best aligns with future strategic
									directions across the enterprise.{' '}
								</p>
							]
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default EnterpriseArchitecture;
